import type { GetEditionsGalleryQuery } from '../../queries/__generated__/getEditionsGallery.generated';
import { getEditionCardLine3 } from '#lib/features/editions/utils/getEditionCardLine3/index.js';
import { EditionsRoutes } from '#lib/websites/editions/constants/routes.js';
import { getImageUrl } from '$global/utils/getImageUrl';

export interface EditionsGalleryCardImage {
	url: string;
	width: number;
	height: number;
}

// A gallery edition as the browse components hand it over: the query row with
// its image resolved to a URL and its artists joined into one display string.
export type EditionsGalleryCardEdition = Omit<
	GetEditionsGalleryQuery['Editions'][0],
	'image' | 'artists'
> & {
	image: EditionsGalleryCardImage;
	artists: string;
};

export interface EditionsGalleryCard {
	line1: string;
	line2: string;
	line3: string;
	href: string;
	alt: string;
	image: EditionsGalleryCardImage;
	// The id reported in select_item tracking events: the edition code, or the
	// series code when the card stands for a whole (sub-)series.
	itemId: string;
}

/**
 * Maps a gallery edition to the card it is rendered as. An edition is usually
 * its own card, but the gallery's base filter also returns one stand-in
 * edition per preview-only series and per sub-series; with the matching flag
 * on, that edition renders as a card for the series it stands for instead.
 */
export const getEditionsGalleryCard = ({
	edition,
	showSeriesOnlyWhenPreview = false,
	showSubSeriesAsCards = false,
}: {
	edition: EditionsGalleryCardEdition;
	showSeriesOnlyWhenPreview?: boolean;
	showSubSeriesAsCards?: boolean;
}): EditionsGalleryCard => {
	const series = edition.series;
	const parentSeries = series?.parent_series?.[0]?.Series_id;

	if (showSubSeriesAsCards && parentSeries) {
		// Sub-series names are bare codes ("H12-1"), so the parent's name leads
		// and the sub-series name takes the code line, mirroring how the series
		// page titles a sub-series by its parent.
		const line2 = parentSeries.name || series?.name || '';
		const subSeriesName = series?.name || series?.code || '';
		const seriesImage = series?.image;

		return {
			line1: edition.artists,
			line2,
			line3: getEditionCardLine3(line2, subSeriesName),
			href: `${EditionsRoutes.Series}/${series?.slug}`,
			alt: `${line2} ${subSeriesName}`.trim(),
			// The sub-series' own image, as the series page shows it; the stand-in
			// edition's image is only a fallback for a sub-series without one.
			image: seriesImage?.filename_disk
				? {
						url: getImageUrl(seriesImage.filename_disk, '512px'),
						width: seriesImage.width || 0,
						height: seriesImage.height || 0,
					}
				: edition.image,
			itemId: series?.code || '',
		};
	}

	const line2 = edition.name || '';
	const alt = `${edition.name} ${edition.code}`;

	if (showSeriesOnlyWhenPreview && series?.only_show_preview_in_gallery) {
		return {
			line1: edition.artists,
			line2,
			line3: getEditionCardLine3(line2, series.code),
			href: `${EditionsRoutes.Series}/${series.slug}`,
			alt,
			image: edition.image,
			itemId: series.code || '',
		};
	}

	return {
		line1: edition.artists,
		line2,
		line3: getEditionCardLine3(line2, edition.code),
		href: `${EditionsRoutes.Artwork}/${edition.code}`,
		alt,
		image: edition.image,
		itemId: edition.code,
	};
};
